import { ChangeEvent, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import useOffers from 'api/useOffers';
import usePricing from 'api/usePricing';
import { useProduct } from 'api/useProduct';
import { type FormikProps, ProductsSequenceProduct } from 'common/contracts';
import { EAssetType, OfferType } from 'constants/enums';
import Decimal from 'decimal.js';
import { transformQuantityValue } from 'utils/formattingUtil';
import { isPriceLocalized as isPriceLocalizedUtil } from 'utils/isPriceLocalized';

import AutoCompleteInput from 'components/AutoCompleteInput/AutoCompleteInput';
import Button from 'components/Button/Button';
import {
  type ImageObject,
  Product
} from 'components/ProductsForm/ProductsForm.types';
import ProductsTable from 'components/ProductsTable/ProductsTable';

import Input from '../../../components/Input/Input';
import RichText from '../../../components/RichText/RichText';
import { ProductProps } from '../CheckoutLinkForm';

export interface SettingsTabProps {
  formikProps: FormikProps;
  products: ProductProps[];
  setProducts: (products: ProductProps[]) => void;
  setDirtyProducts: (isDirty: boolean) => void;
  setCurrentProduct?: (product: ProductsSequenceProduct | null) => void;
  isLoadingProducts?: boolean;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
  formikProps,
  products,
  setProducts,
  setDirtyProducts
}) => {
  const { handleChange, handleBlur, values, touched, errors } = formikProps;
  const { publisherId } = useParams();

  const { getPricings } = usePricing();
  const priceTypingDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isPriceLocalized, setIsPriceLocalized] = useState(false);
  if (!getPricings.data && !getPricings.isRefetching) {
    getPricings.refetch();
  }

  const [currentProduct, setCurrentProduct] =
    useState<ProductsSequenceProduct | null>(null);
  const { data: productsData, isLoading: isLoadingProducts } =
    useProduct(publisherId).getProducts;
  const productsList: Product[] = productsData?.result;
  const { formatProductQuantity } = useOffers({
    offerId: values.offerId,
    offerType: OfferType.CHECKOUT_LINK
  });
  const getProductsOptions = useMemo(() => {
    if (isLoadingProducts || !productsList) return [];
    const productIds = new Set(products.map((item) => item.productId));
    const filteredProducts = productsList.filter(
      (product: Product) => !productIds.has(product.productId as string)
    );
    if (filteredProducts.length === 0) return [];
    return filteredProducts.map((product) => ({
      url: product.images?.[0]?.url,
      name: product.name,
      key: product.name,
      value: product.productId
    }));
  }, [productsList, products, isLoadingProducts]);

  const selectedProduct =
    getProductsOptions.find(
      (product) => product.value === currentProduct?.productId
    ) || null;
  const handleQuantityChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newQuantity =
      e.target.value.length !== 0
        ? transformQuantityValue(e.target.value)
        : e.target.value;
    setCurrentProduct({
      productId: currentProduct?.productId || '',
      quantity: newQuantity
    });
  };

  const addProduct = async () => {
    if (!currentProduct?.productId || !currentProduct?.quantity) return;

    setDirtyProducts(true);
    const foundProduct = productsData!.result.find((pd: Product) => {
      return pd.productId === currentProduct?.productId;
    });
    const formattedProductQuantity = await formatProductQuantity.mutateAsync({
      quantity: currentProduct!.quantity.toString(),
      productType: foundProduct.type
    });
    setProducts([
      ...products,
      {
        priority: 'main',
        _id: currentProduct!.productId!,
        productId: currentProduct!.productId!,
        image: foundProduct!.images[0].url,
        imagePrefix: foundProduct.images?.find(
          (i: ImageObject) => i.type === EAssetType.PRODUCT_PREFIX
        )?.url,
        name: foundProduct!.name,
        amount: currentProduct!.quantity,
        type: foundProduct!.type,
        textFontColorHex: foundProduct!.textFontColorHex,
        prefix: foundProduct!.prefix,
        suffix: foundProduct!.suffix,
        quantityDisplay: formattedProductQuantity.data
      }
    ]);
    setCurrentProduct({
      productId: '',
      quantity: 0
    });
  };

  const priceLocalizedCheckDebounce = (price: number) => {
    if (priceTypingDebounceTimeout.current) {
      clearTimeout(priceTypingDebounceTimeout.current);
    }
    priceTypingDebounceTimeout.current = setTimeout(() => {
      if (getPricings.data && price) {
        setIsPriceLocalized(
          isPriceLocalizedUtil(price, getPricings.data.result)
        );
      } else {
        setIsPriceLocalized(true);
      }
    }, 1000);
  };

  return (
    <div className="w-[456px] mx-auto">
      <RichText
        title="General"
        description="Start with setting up your offer mandatory fields"
        className="mb-9"
      />
      <div className="flex flex-col gap-5 mb-4">
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="Name"
            placeholder="Name"
            type="text"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={touched.name && errors.name ? 'error' : 'default'}
            description={touched.name && errors.name ? errors.name : ''}
          />
          <Input
            label="Display Name"
            placeholder="Display Name"
            type="text"
            name="displayName"
            value={values.displayName}
            onChange={handleChange}
          />
          <Input
            label="External ID"
            placeholder="ID Number"
            type="text"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={
              touched.publisherOfferId && errors.publisherOfferId
                ? 'error'
                : 'default'
            }
            description={
              touched.publisherOfferId && errors.publisherOfferId
                ? errors.publisherOfferId
                : ''
            }
          />
          <Input
            label="Price"
            placeholder="29.99"
            type="number"
            name="price"
            value={values.price}
            onChange={(e) => {
              handleChange(e);
              if (e.target.value) {
                priceLocalizedCheckDebounce(
                  new Decimal(e.target.value).times(100).toNumber()
                );
              }
            }}
            isRequired
            state={touched.price && errors.price ? 'error' : 'default'}
            description={touched.price && errors.price ? errors.price : ''}
            leftIcon={<span>$</span>}
            leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
            min={0.8}
          />
        </div>
      </div>
      <div className="my-5 border-t" />

      <RichText
        title="Products"
        description="Add products to your new offer"
        className="mb-9"
      />

      <div className="flex flex-col gap-5 mb-9 w-full">
        <div className="grid grid-cols-2 gap-5">
          <AutoCompleteInput
            label="Product name"
            placeholder="Search product"
            options={getProductsOptions}
            onChange={(newValue: any) => {
              setCurrentProduct({
                ...currentProduct,
                productId: newValue?.value || '',
                quantity: currentProduct?.quantity || 0
              });
            }}
            currentOption={selectedProduct}
          />

          <Input
            label="Quantity"
            placeholder="Set quantity"
            type="number"
            value={currentProduct?.quantity}
            onChange={(e) => {
              const value = e.target.value;
              const parsedValue = Number(value);

              if (value === '' || (!isNaN(parsedValue) && parsedValue >= 0)) {
                setCurrentProduct({
                  ...currentProduct,
                  quantity: parsedValue
                });
                handleQuantityChange(e);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'Minus') {
                e.preventDefault();
              }
            }}
            min={0}
          />
        </div>
        <Button
          isCreateButton
          onClick={addProduct}
          disabled={!currentProduct?.productId || !currentProduct?.quantity}
          className="w-full bg-base-accent text-base-accentForeground"
        >
          Add Product
        </Button>
      </div>

      <ProductsTable
        products={products}
        setProducts={setProducts}
        setDirtyProducts={setDirtyProducts}
        setCurrentProduct={setCurrentProduct}
        formikProps={formikProps}
        isLoadingProducts={isLoadingProducts}
      />
    </div>
  );
};

export default SettingsTab;
